/**
 * Seed a candidate theme_config from a Look preset.
 *
 * A Look's `font_pair_id` and `palette_family_id` are high-level "feel" hints —
 * they are NOT applied at runtime (PublicLayout reads concrete fonts/colors from
 * the theme_config font and color fields). So when a sandbox user picks a
 * gallery Look we translate those hints into concrete, runtime-applicable
 * values, otherwise the live preview would change layout/shape but keep the
 * old colors and fonts.
 *
 * The font names map to the Google Font families documented in `types.ts`; the
 * palette families map to representative primary/accent/warm-bg colors. These
 * are tasteful starting points the user can fine-tune — not an exact rendering
 * of every preset.
 */

import { LOOKS } from './presets';
import type { FontPairId, LookConfig, PaletteFamilyId, PresetId } from './types';

/**
 * The runtime-applicable subset of `site_settings.theme_config` a Look can
 * seed. Structurally a `DesignThemeConfig` (web/src/stores/design-preview-store.ts)
 * minus the fields only the sandbox sets.
 */
export interface ThemeConfigSeed {
  look: LookConfig;
  font_heading: string;
  font_body: string;
  primary_color: string;
  accent_color: string;
  bg_warm: string;
}

/** font_pair_id → concrete { heading (display), body (sans) } Google families. */
export const FONT_PAIRS: Record<FontPairId, { heading: string; body: string }> = {
  classic_serif: { heading: 'Playfair Display', body: 'Inter' },
  editorial: { heading: 'DM Serif Display', body: 'DM Sans' },
  modern_sans: { heading: 'Manrope', body: 'Manrope' },
  cozy_warm: { heading: 'Fraunces', body: 'Inter' },
  bold_grotesk: { heading: 'Space Grotesk', body: 'Inter' },
  museum: { heading: 'Cormorant Garamond', body: 'Source Sans 3' },
  playful: { heading: 'Fredoka', body: 'Nunito' },
  tech: { heading: 'Space Grotesk', body: 'Inter' },
};

/**
 * palette_family_id → representative { primary, accent, bg_warm }.
 * primary maps to --color-sage-700, accent to --color-terra, bg_warm to
 * --bg-warm (the three runtime-overridable color vars). warm_earth mirrors the
 * current LSG default so picking it reads as "no big color change".
 */
export const PALETTE_FAMILIES: Record<PaletteFamilyId, { primary: string; accent: string; bg_warm: string }> = {
  warm_earth: { primary: '#4a7c59', accent: '#c4602f', bg_warm: '#faf6ee' },
  cool_slate: { primary: '#475569', accent: '#0ea5e9', bg_warm: '#f1f5f9' },
  jewel: { primary: '#047857', accent: '#b45309', bg_warm: '#fdf6ec' },
  monochrome: { primary: '#1f2937', accent: '#6b7280', bg_warm: '#f5f5f4' },
  pastel: { primary: '#db7093', accent: '#a78bfa', bg_warm: '#fdf2f8' },
  vibrant: { primary: '#4f46e5', accent: '#f97316', bg_warm: '#fff7ed' },
  editorial_ivory: { primary: '#292524', accent: '#b91c1c', bg_warm: '#fbf9f4' },
  gradient_tech: { primary: '#4338ca', accent: '#06b6d4', bg_warm: '#eef2ff' },
};

/**
 * Build a full candidate theme_config from a preset: the Look bundle plus the
 * concrete colors and fonts implied by its palette family + font pair.
 */
export function presetToThemeConfig(presetId: PresetId): ThemeConfigSeed {
  const preset = LOOKS[presetId];
  const fonts = FONT_PAIRS[preset.font_pair_id];
  const palette = PALETTE_FAMILIES[preset.palette_family_id];
  return {
    look: { preset_id: presetId },
    font_heading: fonts.heading,
    font_body: fonts.body,
    primary_color: palette.primary,
    accent_color: palette.accent,
    bg_warm: palette.bg_warm,
  };
}
